import { employerRights, zywaveUserRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { action, makeAutoObservable } from 'mobx';
import { createContext, useContext } from 'react';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { ChecklistButtonStateText, ChecklistCompletionStatus } from 'types/enrollmentService';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { QUOTE_ROUTES, QuoteRouteV2Id } from 'routes/routesConfigV2';
import { bpHistory } from 'global/bpHistory';
import { QUOTE_CHECKLIST_MAPPING } from 'store/flows/quote/checklistMapping';
import { redirectToRoute } from 'routes/redirectToRoute';

interface ChecklistOptionPresenter {
  title: string;
  text: string;
  buttonState: ChecklistButtonStateText;
  completionStatus: ChecklistCompletionStatus;
  onActionClick: () => void;
}

export class QuoteChecklistPageStore {
  private rootStore: RootStore = getRootStoreInstance();
  public readonly flowDelegate: QuoteFlowDelegate;
  private _domainParams: QuoteFlowDomainParams;

  get checklistOptions(): ChecklistOptionPresenter[] {
    if (this.flowDelegate.enrollmentPeriodModule.enrollmentProposalData === null) return [];

    const proposalChecklist = this.flowDelegate.enrollmentPeriodModule.quoteProposalChecklistData(
      this._domainParams.cartId,
    );

    if (!proposalChecklist) {
      throw new Error(`No proposal with id: ${this._domainParams.cartId}!`);
    }

    return proposalChecklist.checklistOptions.map((option) => {
      const card = option.card;
      const page = QUOTE_ROUTES[QUOTE_CHECKLIST_MAPPING[card] as QuoteRouteV2Id];

      if (!page) {
        throw new Error(`No page found for card: ${card}!`);
      }

      return {
        title: page.title,
        text: page.subTitle,
        buttonState: option.buttonState,
        completionStatus: option.completionStatus,
        onActionClick: action((): void => {
          redirectToRoute(QUOTE_CHECKLIST_MAPPING[card], this._domainParams);
        }),
      };
    });
  }

  public onBackRedirect(): void {
    if (this.flowDelegate.isFlowDataLoading || !this.flowDelegate.enrollmentPeriodModule.enrollmentCoreData) {
      return;
    }
    if (employerRights(this.rootStore.userStore.userRoles)) {
      bpHistory.push('/employee');
      return;
    }
    if (zywaveUserRights(this.rootStore.userStore.userRoles)) {
      bpHistory.push(`/zywave-group/${this._domainParams.groupId}`);
      return;
    }
    bpHistory.push(`/group/${this._domainParams.groupId}`);
  }

  public async getInitialData(): Promise<void> {
    await this.flowDelegate.getFlowInitialData();
  }

  constructor(params: QuoteFlowDomainParams) {
    this._domainParams = params;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const QuoteChecklistPageStoreContext = createContext(new QuoteChecklistPageStore({} as QuoteFlowDomainParams));

export const useQuoteChecklistPageStore = (): QuoteChecklistPageStore => {
  return useContext(QuoteChecklistPageStoreContext);
};

export { QuoteChecklistPageStoreContext };
